import React, { createContext, useState, useCallback, ReactNode } from 'react';
import GlobalSnackbar from '../component/snackbar/GlobalSnackbar';

export type TSnackbarType = 'success' | 'error';

interface ISnackbarContextType {
    showSnackbar: (message: string, type: TSnackbarType) => void;
    showSuccess: (message: string) => void;
    showError: (message: string) => void;
}

interface ISnackbarState {
    open: boolean;
    message: string;
    type: TSnackbarType;
    key: number;
}

const SnackbarContext = createContext<ISnackbarContextType | undefined>(undefined);

interface ISnackbarProviderProps {
    children: ReactNode;
}

const defaultSnackbarValue: ISnackbarState = {
    open: false,
    message: "",
    type: "success",
    key: 0
}

export const SnackbarProvider: React.FC<ISnackbarProviderProps> = ({ children }) => {

    const [snackbar, setSnackbar] = useState<ISnackbarState>(defaultSnackbarValue);

    const showSnackbar = useCallback((message: string, type: TSnackbarType) => {
        setSnackbar((prev) => ({
            open: true,
            message,
            type,
            key: prev.key + 1,
        }));
    }, []);

    const showSuccess = useCallback((message: string) => {
        showSnackbar(message, 'success');
    }, [showSnackbar]);

    const showError = useCallback((message: string) => {
        showSnackbar(message, 'error');
    }, [showSnackbar]);

    const handleClose = useCallback(() => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    }, []);

    return (
        <SnackbarContext.Provider
            value={{
                showSnackbar,
                showSuccess,
                showError,
            }}
        >
            {children}
            <GlobalSnackbar
                key={snackbar.key}
                open={snackbar.open}
                message={snackbar.message}
                type={snackbar.type}
                onClose={handleClose}
            />
        </SnackbarContext.Provider>
    );
};

export const useSnackbar = (): ISnackbarContextType => {
    const context = React.useContext(SnackbarContext);
    if (!context) {
        throw new Error('useSnackbar must be used within a SnackbarProvider');
    }
    return context;
};
